"use client";

import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { forwardRef, useImperativeHandle, useRef } from "react";

type TurnstileWidgetProps = {
  onSuccess: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  className?: string;
};

export type TurnstileWidgetRef = {
  reset: () => void;
};

/**
 * Reusable Cloudflare Turnstile widget wrapper.
 *
 * Features:
 * - Conditional rendering when not configured (dev mode)
 * - onExpire handler to reset token state
 * - Ref for manual reset after failed submissions
 * - Auto theme and flexible sizing
 *
 * Usage:
 * ```tsx
 * const turnstileRef = useRef<TurnstileWidgetRef>(null);
 * const [token, setToken] = useState<string | null>(null);
 *
 * // Reset on error:
 * turnstileRef.current?.reset();
 *
 * <TurnstileWidget
 *   ref={turnstileRef}
 *   onSuccess={setToken}
 *   onExpire={() => setToken(null)}
 * />
 * ```
 */
export const TurnstileWidget = forwardRef<TurnstileWidgetRef, TurnstileWidgetProps>(function TurnstileWidget(
  { onSuccess, onExpire, onError, className },
  ref
) {
  const turnstileRef = useRef<TurnstileInstance>(null);

  // Expose reset method to parent
  useImperativeHandle(ref, () => ({
    reset: () => {
      turnstileRef.current?.reset();
    },
  }));

  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  // Don't render if Turnstile is not configured (dev mode)
  if (!siteKey) {
    return null;
  }

  return (
    <Turnstile
      className={className}
      onError={onError}
      onExpire={onExpire}
      onSuccess={onSuccess}
      options={{
        theme: "auto",
        size: "flexible",
      }}
      ref={turnstileRef}
      siteKey={siteKey}
    />
  );
});
